/**
 * Temporary Access Service
 * Implements the same temporary access logic as the backend using Firestore
 * Creates and manages temporary access links directly in Firestore
 */

import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  runTransaction
} from 'firebase/firestore';
import { db } from '../firebase-config';
import cloudFunctionsService from './cloudFunctionsService';

class TempAccessService {
  constructor() {
    this.db = db;
  }

  /**
   * Create a new temporary access link by calling the backend cloud function
   * Falls back to direct Firestore creation if backend is unavailable (CORS)
   */
  async createTempAccess(email, durationHours, role = 'guest', createdBy, autoTimeout = false) {
    try {
      console.log('🔄 Calling backend to create temporary access for:', email);
      
      const durationSeconds = durationHours * 60 * 60;

      const result = await cloudFunctionsService.callCloudFunction('generateTempAccess', {
        email,
        duration: durationSeconds,
        role,
        createdBy,
        autoTimeout,
      });

      console.log('✅ Backend responded successfully:', result);

      // The backend now handles email sending, but we can double-check or log here.
      if (result.status === 'success') {
        console.log('📧 Temporary access email queued for sending by backend.');
        
        // The backend returns expiresAt as an ISO string. We convert it to a Date object.
        const expiresAt = new Date(result.expiresAt);

        return {
          success: true,
          accessToken: result.accessToken,
          accessLink: result.accessLink,
          expiresAt: expiresAt,
          email: result.email,
          role: result.role,
        };
      } else {
        throw new Error(result.message || 'Unknown error from backend');
      }
    } catch (error) {
      // If CORS error, fall back to direct Firestore creation
      if (error.message === 'CORS_ERROR' || error.message.includes('Failed to fetch') || error.message.includes('CORS')) {
        console.log('⚠️ Backend unavailable (CORS), creating temporary access directly in Firestore...');
        return await this.createTempAccessDirectly(email, durationHours, role, createdBy, autoTimeout);
      }
      
      // For other errors, log and return failure
      console.error('❌ Error creating temporary access via backend:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Create temporary access directly in Firestore (fallback when backend is unavailable)
   */
  async createTempAccessDirectly(email, durationHours, role = 'guest', createdBy, autoTimeout = false) {
    try {
      console.log('🔄 Creating temporary access directly in Firestore...');
      
      // Generate a unique access token
      const accessToken = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      
      // Calculate expiration time
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + durationHours);
      
      // Create the access link
      const baseUrl = window.location.origin;
      const accessLink = `${baseUrl}/temp-access/${accessToken}`;
      
      // Store in Firestore
      const tempAccessRef = doc(this.db, 'temporary_access', accessToken);
      await setDoc(tempAccessRef, {
        email: email.trim().toLowerCase(),
        role: role,
        duration: durationHours,
        expiresAt: expiresAt,
        createdAt: serverTimestamp(),
        createdBy: createdBy,
        isActive: true,
        accessCount: 0,
        accessLink: accessLink,
        autoTimeout: autoTimeout,
      });
      
      console.log('✅ Temporary access created directly in Firestore');
      
      return {
        success: true,
        accessToken: accessToken,
        accessLink: accessLink,
        expiresAt: expiresAt,
        email: email.trim().toLowerCase(),
        role: role,
      };
    } catch (error) {
      console.error('❌ Error creating temporary access directly in Firestore:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Get all temporary access links created by a user
   */
  async getTempAccessLinks(createdBy) {
    try {
      console.log('📡 Fetching temporary access links for:', createdBy);
      
      const tempAccessQuery = query(
        collection(this.db, 'temporary_access'),
        where('createdBy', '==', createdBy),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(tempAccessQuery);
      const links = [];
      
      snapshot.forEach(doc => {
        const data = doc.data();
        links.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || data.createdAt,
          expiresAt: data.expiresAt?.toDate?.() || data.expiresAt
        });
      });
      
      console.log('✅ Fetched', links.length, 'temporary access links');
      return links;
      
    } catch (error) {
      console.error('❌ Error fetching temporary access links:', error);
      return [];
    }
  }

  /**
   * Validate a temporary access token
   */
  async validateTempAccess(accessToken) {
    try {
      console.log('🔍 Validating temporary access token:', accessToken);
      
      const tempAccessRef = doc(this.db, 'temporary_access', accessToken);

      const result = await runTransaction(this.db, async (transaction) => {
        const tempAccessDoc = await transaction.get(tempAccessRef);

        if (!tempAccessDoc.exists()) {
          console.log('❌ Temporary access token not found');
          return { valid: false, error: 'Token not found' };
        }

        const data = tempAccessDoc.data();

        if (!data.isActive) {
          console.log('❌ Temporary access token is inactive');
          return { valid: false, error: 'Token is inactive' };
        }

        const now = new Date();
        const expiresAt = data.expiresAt?.toDate?.() || data.expiresAt;

        if (now > expiresAt) {
          console.log('❌ Temporary access token has expired');
          transaction.update(tempAccessRef, { isActive: false });
          return { valid: false, error: 'Token has expired' };
        }

        transaction.update(tempAccessRef, {
          accessCount: (data.accessCount || 0) + 1,
          lastAccessedAt: serverTimestamp()
        });

        console.log('✅ Temporary access token is valid');
        return {
          valid: true,
          email: data.email,
          role: data.role,
          expiresAt: expiresAt,
          accessCount: (data.accessCount || 0) + 1
        };
      });

      return result;

    } catch (error) {
      console.error('❌ Error validating temporary access token:', error);
      return { valid: false, error: error.message };
    }
  }

  /**
   * Generate and email a shared admin OTP for a temp access token
   * - Stores OTP in otp_verification with id: admin_temp_access_{token}
   * - Emails all admins from admin_temp_access collection
   */
  async generateAdminOTP(accessToken, tempLinkEmail) {
    try {
      console.log('🔄 Calling backend to generate admin OTP for:', tempLinkEmail);

      const result = await cloudFunctionsService.callCloudFunction('generateAdminTempAccessOTP', {
        accessToken,
        tempLinkEmail,
      });

      if (result.status === 'success') {
        console.log('✅ Backend generated and sent admin OTPs successfully.');
        return { success: true };
      } else {
        throw new Error(result.message || 'Unknown error from backend OTP generation');
      }
    } catch (error) {
      console.error('❌ Error generating admin OTP via backend:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Verify admin OTP for a temp access token
   */
  async verifyAdminOTP(accessToken, otpCode) {
    try {
      console.log('🔍 Verifying admin OTP via backend for access token:', accessToken);

      const result = await cloudFunctionsService.callCloudFunction('verifyAdminTempAccessOTP', {
        accessToken,
        otp: otpCode,
      });

      if (result.status === 'success') {
        console.log('✅ Backend verified admin OTP successfully.');
        return { 
          valid: true, 
          tempLinkEmail: result.tempLinkEmail, 
          accessToken 
        };
      } else {
        // Use the error message from the backend
        throw new Error(result.message || 'Unknown error from backend OTP verification');
      }
    } catch (error) {
      console.error('❌ Error verifying admin OTP via backend:', error);
      return { valid: false, error: error.message };
    }
  }

  /**
   * Get admin recipients from admin_temp_access collection
   */
  async getAdminUsers() {
    try {
      const snap = await getDocs(collection(this.db, 'admin_temp_access'));
      const admins = [];
      for (const d of snap.docs) {
        const data = d.data();
        if (data?.email) {
          admins.push({ uid: d.id, email: data.email, displayName: data.displayName || data.email.split('@')[0] });
        }
      }
      return admins;
    } catch (error) {
      console.error('❌ Error loading admin_temp_access:', error);
      return [];
    }
  }

  /**
   * Revoke a temporary access token
   */
  async revokeTempAccess(accessToken) {
    try {
      console.log('🔄 Revoking temporary access token:', accessToken);
      
      await updateDoc(doc(this.db, 'temporary_access', accessToken), {
        isActive: false,
        revokedAt: serverTimestamp()
      });
      
      console.log('✅ Temporary access token revoked successfully');
      return { success: true };
      
    } catch (error) {
      console.error('❌ Error revoking temporary access token:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete a temporary access token
   */
  async deleteTempAccess(accessToken) {
    try {
      console.log('🗑️ Deleting temporary access token:', accessToken);
      
      await deleteDoc(doc(this.db, 'temporary_access', accessToken));
      
      console.log('✅ Temporary access token deleted successfully');
      return { success: true };
      
    } catch (error) {
      console.error('❌ Error deleting temporary access token:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Clean up expired tokens
   */
  async cleanupExpiredTokens() {
    try {
      console.log('🧹 Cleaning up expired temporary access tokens');
      
      const now = new Date();
      const tempAccessQuery = query(
        collection(this.db, 'temporary_access'),
        where('isActive', '==', true)
      );
      
      const snapshot = await getDocs(tempAccessQuery);
      const batch = [];
      
      snapshot.forEach(doc => {
        const data = doc.data();
        const expiresAt = data.expiresAt?.toDate?.() || data.expiresAt;
        
        if (now > expiresAt) {
          batch.push(updateDoc(doc.ref, { isActive: false }));
        }
      });
      
      if (batch.length > 0) {
        await Promise.all(batch);
        console.log('✅ Cleaned up', batch.length, 'expired tokens');
      } else {
        console.log('✅ No expired tokens found');
      }
      
    } catch (error) {
      console.error('❌ Error cleaning up expired tokens:', error);
    }
  }
}

export default new TempAccessService();
